<template>
  <div class="new-oa-workflow-form-edit-container">
    <el-card class="box-card">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('newOaWorkflowFormEdit.title') }}</span>
      </div>
      <el-form ref="form" :model="formData" class="text-left" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="24">
            <div v-for="(item, index) in newOaWorkflowFormEditInfo.components" :key="index">
              <!-- 文本框 -->
              <el-form-item v-if="item.type === 'textfield'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-input v-model="formData[item.key]"  :readonly="isReadonlyField(item)" :placeholder="item.description" clearable />
              </el-form-item>

              <!-- 数字框 -->
              <el-form-item v-if="item.type === 'number'" :label="item.label" 
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-input-number v-model="formData[item.key]" :min="0" :precision="2" :max="99999999999" :step="0.01" :placeholder="item.description"
                  controls-position="right" style="width: 100%" />
              </el-form-item>

              <!-- 文本域 -->
              <el-form-item v-if="item.type === 'textarea'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-input v-model="formData[item.key]" type="textarea" :placeholder="item.description" 
                  :rows="4" />
              </el-form-item>

              <!-- 日期选择 -->
              <el-form-item v-if="item.type === 'textdate'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-date-picker v-model="formData[item.key]" type="date" :placeholder="item.description"
                  value-format="yyyy-MM-dd" style="width: 100%" />
              </el-form-item>

              <!-- 日期时间选择 -->
              <el-form-item v-if="item.type === 'textdatetime'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-date-picker v-model="formData[item.key]" type="datetime" :placeholder="item.description"
                  value-format="yyyy-MM-dd HH:mm:ss" style="width: 100%" />
              </el-form-item>

              <!-- 单选框 -->
              <el-form-item v-if="item.type === 'radio'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-radio-group v-model="formData[item.key]">
                  <el-radio v-for="(option, optionIndex) in item.values" :key="optionIndex"
                    :label="option.value">
                    {{ option.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <!-- 下拉选择 -->
              <el-form-item v-if="item.type === 'select'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-select v-model="formData[item.key]" :placeholder="item.description" 
                  style="width: 100%" clearable>
                  <el-option v-for="(option, optionIndex) in item.values" :key="optionIndex"
                    :label="option.label" :value="option.value">
                  </el-option>
                </el-select>
              </el-form-item>

              <!-- 复选框 - 修复数组类型问题 -->
              <el-form-item v-if="item.type === 'checkbox'" :label="item.label"
                :prop="item.key"
                :rules="getValidationRules(item)">
                <el-checkbox-group v-model="formData[item.key]">
                  <el-checkbox v-for="(option, optionIndex) in item.values" :key="optionIndex"
                    :label="option.value">
                    {{ option.label }}
                  </el-checkbox>
                </el-checkbox-group>
              </el-form-item>
            </div>

            <!-- 文件上传 -->
            <el-form-item :label="$t('common.file')">
              <div class="upload-section">
                <upload-file ref="uploadFile" @notify="handleFileUpload" :show-file-list="false" />
              </div>
              <div class="attachment-list" v-if="fileList.length > 0">
                <div class="attachment-item" v-for="(file, index) in fileList" :key="index">
                  <i :class="isImage(file.fileName) ? 'el-icon-picture' : 'el-icon-document'"></i>
                  <span class="attachment-name" @click="isImage(file.fileName) ? previewImage(file.url) : downloadFile(file.url, file.fileName)">{{ file.fileName }}</span>
                  <el-button type="text" icon="el-icon-delete" @click="removeFile(index)" class="attachment-delete"></el-button>
                </div>
              </div>
              <!-- 图片预览 -->
              <el-dialog :visible.sync="imagePreviewVisible" width="600px" append-to-body>
                <img :src="imagePreviewUrl" style="width: 100%;" />
              </el-dialog>
            </el-form-item>

            <!-- 按钮区域 -->
            <div class="form-footer">
              <el-button type="primary" @click="_submitFormData" :loading="submitLoading">
                <i class="el-icon-check"></i>
                {{ $t('newOaWorkflowFormEdit.submit') }}
              </el-button>
              <el-button type="warning" @click="closeEditInfo">
                {{ $t('newOaWorkflowFormEdit.cancel') }}
              </el-button>
            </div>
          </el-col>
        </el-row>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { queryOaWorkflowForm, queryOaWorkflowFormData, updateOaWorkflowFormData } from '@/api/oa/newOaWorkflowFormEditApi'
import { auditOaWorkflow } from '@/api/oa/newOaWorkflowDetailApi'
import UploadFile from '@/components/upload/uploadFile'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'NewOaWorkflowFormEditList',
  components: {
    UploadFile
  },
  data() {
    return {
      newOaWorkflowFormEditInfo: {
        formJson: {},
        components: [],
        conditions: {},
        flowId: '',
        id: '',
        fileName: '',
        realFileName: '',
        state: ''
      },
      formData: {}, // 新增：用于表单数据绑定
      fileList: [], // 附件列表
      taskId: '',
      submitLoading: false,
      communityId: '',
      imagePreviewVisible: false,
      imagePreviewUrl: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.newOaWorkflowFormEditInfo.flowId = this.$route.query.flowId
    this.newOaWorkflowFormEditInfo.id = this.$route.query.id
    this.taskId = this.$route.query.taskId || ''
    this._listOaWorkflowFormEdit(1, 100)
  },
  methods: {
    isReadonlyField(item) {
      return item.label === '申请人' || item.label === '申请人部门'
    },
    async _listOaWorkflowFormEdit(page, rows) {
      try {
        const params = {
          page,
          row: rows,
          flowId: this.newOaWorkflowFormEditInfo.flowId
        }

        const { data } = await queryOaWorkflowForm(params)
        const formData = JSON.parse(data[0].formJson)
        this.newOaWorkflowFormEditInfo.formJson = formData
        this.newOaWorkflowFormEditInfo.components = formData.components
        
        // 初始化表单数据
        this.initFormData()
        
        this._listOaWorkflowDetails()
      } catch (error) {
        console.error('获取表单数据失败:', error)
        this.$message.error('获取表单数据失败')
      }
    },

    initFormData() {
      // 初始化表单数据对象
      this.formData = {}
      this.newOaWorkflowFormEditInfo.components.forEach(item => {
        if (item.type !== 'button' && item.type !== 'text') {
          if (item.type === 'checkbox') {
            // 确保复选框的值是数组类型
            this.$set(this.formData, item.key, [])
          } else if (item.type === 'radio' || item.type === 'select') {
            this.$set(this.formData, item.key, item.values && item.values.length > 0 ? item.values[0].value : '')
          } else {
            this.$set(this.formData, item.key, '')
          }
        }
      })
    },

    async _listOaWorkflowDetails() {
      try {
        const params = {
          page: 1,
          row: 1,
          id: this.newOaWorkflowFormEditInfo.id,
          flowId: this.newOaWorkflowFormEditInfo.flowId
        }

        const { data } = await queryOaWorkflowFormData(params)
        const detailData = data[0]

        // 保存状态和taskId，用于后续判断是否需要调用审核接口
        this.newOaWorkflowFormEditInfo.state = detailData.state || ''

        // 更新表单数据
        this.newOaWorkflowFormEditInfo.components.forEach(item => {
          if (item.type !== 'button' && item.type !== 'text') {
            const value = detailData[item.key]
            if (value !== undefined && value !== null) {
              if (item.type === 'checkbox') {
                // 确保复选框数据是数组类型
                if (Array.isArray(value)) {
                  this.$set(this.formData, item.key, value)
                } else if (typeof value === 'string' && value) {
                  // 如果是字符串，转换为数组
                  this.$set(this.formData, item.key, [value])
                } else {
                  this.$set(this.formData, item.key, [])
                }
              } else {
                this.$set(this.formData, item.key, value)
              }
            }
          }
        })

        // 处理文件数据
        if (detailData.files && detailData.files.length > 0) {
          this.fileList = detailData.files.map(file => ({
            fileName: file.fileName,
            realFileName: file.realFileName,
            url: `/callComponent/download/getFile/file?fileId=${file.realFileName}&communityId=${this.communityId}`
          }))
        }
      } catch (error) {
        console.error('获取表单详情失败:', error)
        this.$message.error('获取表单详情失败')
      }
    },

    getValidationRules(item) {
      const rules = []
      if (item.validate && item.validate.required) {
        rules.push({
          required: true,
          message: `${item.label}不能为空`,
          trigger: 'blur'
        })
      }
      return rules
    },

    async _submitFormData() {
      try {
        // 表单验证
        await this.$refs.form.validate()
        
        this.submitLoading = true
        
        const formData = {
          id: this.newOaWorkflowFormEditInfo.id,
          flowId: this.newOaWorkflowFormEditInfo.flowId,
          fileName: this.fileList.map(f => f.fileName).join(','),
          realFileName: this.fileList.map(f => f.realFileName).join(','),
        }

        // 收集表单数据
        this.newOaWorkflowFormEditInfo.components.forEach(item => {
          if (item.type !== 'button' && item.type !== 'text') {
            formData[item.key] = this.formData[item.key]
            if (item.type === 'checkbox') {
              // 复选框提交时取第一个值或空字符串
              formData[item.key] = this.formData[item.key] && this.formData[item.key].length > 0 
                ? this.formData[item.key][0] 
                : ''
            }
          }
        })

        const res = await updateOaWorkflowFormData(formData)
        if (res.code === 0) {
          // 如果申请状态为退回(1004)，修改完成后调用审批提交接口，默认为同意
          if (this.newOaWorkflowFormEditInfo.state === '1003' && this.taskId && this.newOaWorkflowFormEditInfo.flowId) {
            const auditData = {
              auditCode: '1100',
              auditMessage: '修改后重新提交',
              staffId: '',
              staffName: '',
              taskId: this.taskId,
              flowId: this.newOaWorkflowFormEditInfo.flowId,
              id: this.newOaWorkflowFormEditInfo.id
            }
            const auditRes = await auditOaWorkflow(auditData)
            if (auditRes.code === 0) {
              this.$message.success(this.$t('newOaWorkflowFormEdit.submitSuccess'))
              this.closeEditInfo()
            } else {
              this.$message.error(auditRes.msg || this.$t('newOaWorkflowFormEdit.submitError'))
            }
          } else {
            this.$message.success(this.$t('newOaWorkflowFormEdit.submitSuccess'))
            this.closeEditInfo()
          }
        } else {
          this.$message.error(res.msg || this.$t('newOaWorkflowFormEdit.submitError'))
        }
      } catch (error) {
        if (error.message) {
          // 表单验证错误
          return
        }
        console.error('提交表单失败:', error)
        this.$message.error(this.$t('newOaWorkflowFormEdit.submitError'))
      } finally {
        this.submitLoading = false
      }
    },

    closeEditInfo() {
      // SSO模式下：清除标记并关闭当前页签
      if (this.$route.query.ssoLoginFlag === 'true' || sessionStorage.getItem('ssoLoginFlag') === 'true') {
        sessionStorage.removeItem('ssoLoginFlag')
        setTimeout(() => {
          window.close()
        }, 500)
      } else {
        const flowId = this.newOaWorkflowFormEditInfo.flowId
        if (flowId) {
          this.$router.push({
            path: '/views/oa/newOaWorkflow',
            query: { flowId, switchValue: 'newOaWorkflowUndo' }
          })
        } else {
          this.$router.go(-1)
        }
      }
    },

    handleFileUpload(data) {
      // 检查是否已存在同名文件
      const exists = this.fileList.some(f => f.fileName === data.fileName)
      if (exists) {
        this.$message.warning('文件已存在：' + data.fileName)
        return
      }
      this.fileList.push({
        fileName: data.fileName,
        realFileName: data.realFileName,
        url: `/callComponent/download/getFile/file?fileId=${data.realFileName}&communityId=${this.communityId}`
      })
    },
    removeFile(index) {
      this.fileList.splice(index, 1)
    },
    isImage(fileName) {
      const ext = fileName.split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)
    },
    previewImage(url) {
      this.imagePreviewUrl = url
      this.imagePreviewVisible = true
    },
    downloadFile(url, fileName) {
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.target = '_blank'
      link.click()
    }
  }
}
</script>

<style lang="scss" scoped>
.new-oa-workflow-form-edit-container {
  padding: 20px;

  .box-card {
    margin-bottom: 20px;
  }

  .form-footer {
    margin-top: 20px;
    text-align: right;
  }

  .el-form-item {
    margin-bottom: 22px;
  }

  .upload-section {
    text-align: left;

    ::v-deep .el-upload {
      text-align: left;
    }
  }

  .attachment-list {
    margin-top: 10px;

    .attachment-item {
      display: flex;
      align-items: center;
      padding: 6px 10px;
      margin-bottom: 4px;
      background-color: #f5f7fa;
      border-radius: 4px;
      font-size: 14px;

      i:first-child {
        margin-right: 6px;
        color: #409eff;
        font-size: 16px;
      }

      .attachment-name {
        flex: 1;
        cursor: pointer;
        color: #606266;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:hover {
          color: #409eff;
        }
      }

      .attachment-delete {
        margin-left: 8px;
        color: #f56c6c;
      }
    }
  }
}
</style>